import React, { useState, SyntheticEvent, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../shared/LanguageService';
import { useUserContext } from '../../../../shared/context/user-context';
import * as Features from '../../../../shared/Features';
import { useTelemetryContext } from '../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../telemetry/utils';
import { OrderFilm } from './OrderFilmApi';
import FilmDelivery, { FilmDeliveryLabels } from './film/components/FilmDelivery';
import { useOrderFilms } from './film/queries/use-order-films';
import { UploadHandlerFactory } from './film/utils/uploadHelper';
import VideoLinks from './links/components/VideoLinks';

type VideoHandlingLabels = FilmDeliveryLabels & {
    fileTab: string;
    linkTab: string;
    noFilmsOrdered: string;
    filmsLoadFailure: string;
    linkPlaceholder: string;
    addLinkButton: string;
    linksSentTo: string;
    sendLinksAsEmailButton: string;
    sendLinksAsEmailPlaceholder: string;
    sendLinksAsEmailSuccess: string;
    sendLinksAsEmailFailure: string;
    sendEmailDynamicError: (translationKey: string) => string;
};

type VideoHandlingProps = {
    orderId: number;
    createUploadHandler: UploadHandlerFactory;
    labels: VideoHandlingLabels;
    isFilmSent: boolean;
};

type TabPanelProps = {
    currentTab: number;
    connectedTab: number;
};

let VideoTab = styled(Tab)(({ theme }) => ({
    backgroundColor: '#f5f5f5',
    fontSize: '14px',
    color: theme.palette.primary.main
}));

function a11yProps(index: number) {
    return {
        id: `video-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    };
}

function TabPanel({ currentTab, connectedTab, children }: TabPanelProps & { children: ReactNode }) {
    return (
        <div
            role="tabpanel"
            hidden={currentTab !== connectedTab}
            id={`video-tabpanel-${connectedTab}`}
            aria-labelledby={`video-tab-${connectedTab}`}
        >
            {children}
        </div>
    );
}

enum VideoTabs {
    File = 0,
    Links = 1
}

export function VideoHandling({
    orderId,
    createUploadHandler,
    labels,
    isFilmSent
}: VideoHandlingProps) {
    let [currentTab, setCurrentTab] = useState<VideoTabs>(VideoTabs.File);
    let { user } = useUserContext();
    let { logger } = useTelemetryContext();

    let { isError, isLoading, data, error } = useOrderFilms(orderId);

    if (isError && logger) {
        let additionalInfo = {
            origin: 'VideoHandling.getOrderFilms',
            userInfo: getUserInfo(user)
        };
        logger.logException(error, additionalInfo);
    }

    function handleChangeTab(event: SyntheticEvent, nextTab: number) {
        setCurrentTab(nextTab);
    }

    if (
        !Features.hasFeature(Features.UploadFilm, user) &&
        !Features.hasFeature(Features.SendFilm, user) &&
        !isFilmSent
    ) {
        return <div>{Translate('orders/video-tab/video-under-processing')}</div>;
    }

    let films: OrderFilm[] = isLoading || !data ? [] : data;

    return (
        <Paper sx={{ width: '346px' }}>
            <Tabs
                value={currentTab}
                onChange={handleChangeTab}
                variant="fullWidth"
                aria-label="Video handling tabs"
            >
                <VideoTab label={labels.fileTab} {...a11yProps(VideoTabs.File)} />
                <VideoTab label={labels.linkTab} {...a11yProps(VideoTabs.Links)} />
            </Tabs>
            <TabPanel currentTab={currentTab} connectedTab={VideoTabs.File}>
                {isError ? (
                    <Box sx={{ padding: '16px' }}>
                        <Typography variant="body2" sx={{ fontSize: '14px' }}>
                            {labels.filmsLoadFailure}
                        </Typography>
                    </Box>
                ) : null}
                {!isLoading && !isError && films.length === 0 ? (
                    <Box sx={{ padding: '16px' }}>
                        <Typography variant="body2" sx={{ fontSize: '14px' }}>
                            {labels.noFilmsOrdered}
                        </Typography>
                    </Box>
                ) : null}
                {films.map((film: OrderFilm) => (
                    <Box key={film.orderRowId} sx={{ padding: '12px 12px 0 12px' }}>
                        <FilmDelivery
                            orderId={orderId}
                            film={film}
                            createUploadHandler={createUploadHandler}
                            labels={labels}
                        />
                    </Box>
                ))}
                {films.length > 0 ? <Box sx={{ height: '12px' }} /> : null}
            </TabPanel>
            <TabPanel currentTab={currentTab} connectedTab={VideoTabs.Links}>
                <VideoLinks
                    labels={{
                        linkPlaceholder: labels.linkPlaceholder,
                        addLinkButton: labels.addLinkButton,
                        sendLinksAsEmailButton: labels.sendLinksAsEmailButton,
                        sendLinksAsEmailPlaceholder: labels.sendLinksAsEmailPlaceholder,
                        linksSentTo: labels.linksSentTo,
                        sendLinksAsEmailSuccess: labels.sendLinksAsEmailSuccess,
                        sendLinksAsEmailFailure: labels.sendLinksAsEmailFailure,
                        sendEmailDynamicError: labels.sendEmailDynamicError
                    }}
                    orderId={orderId}
                />
            </TabPanel>
        </Paper>
    );
}

function TranslatedVideoHandling(props: VideoHandlingProps) {
    let labels: VideoHandlingLabels = {
        fileTab: Translate('view-order/basic-order-info/panel-headers/film-file'),
        linkTab: Translate('view-order/basic-order-info/panel-headers/film-link'),
        noFilmsOrdered: Translate('view-order/basic-order-info/film/no-films-ordered'),
        filmsLoadFailure: Translate('500-error/description'),
        chooseFileButtonText: Translate('view-order/basic-order-info/film/upload-button/text'),
        addFileButtonText: Translate('view-order/basic-order-info/film/add-file-button/text'),
        downloadButtonTooltip: Translate(
            'view-order/basic-order-info/film/download-button/tooltip'
        ),
        removeButtonTooltip: Translate('view-order/basic-order-info/film/remove-button/tooltip'),
        removeSuccess: Translate('view-order/basic-order-info/film/remove-success'),
        removeFailure: Translate('view-order/basic-order-info/film/remove-failure'),
        deliveredLabel: Translate('view-order/basic-order-info/film/delivered'),
        notDeliveredLabel: Translate('view-order/basic-order-info/film/not-delivered'),
        uploadedLabel: Translate('view-order/basic-order-info/film/film-uploaded/text'),
        uploadSuccess: Translate('view-order/basic-order-info/film/upload-success'),
        uploadFailure: Translate('view-order/basic-order-info/film/upload-failure'),
        sendFilmAsEmailSuccess: Translate('view-order/basic-order-info/film/film-sent/success'),
        sendFilmAsEmailFailure: Translate('view-order/basic-order-info/film/links-sent/failure'),
        sendFilmAsEmailButton: Translate('view-order/basic-order-info/film/send-button/text'),
        sendFilmEmailPlaceholder: Translate(
            'view-order/basic-order-info/film/send-button/placeholder'
        ),
        sendFilmInformationLabel: Translate('view-order/basic-order-info/film/film-sent/text'),
        addLinkButton: Translate('view-order/basic-order-info/film/add-link-button'),
        linkPlaceholder: Translate('view-order/basic-order-info/film/link-input-placeholder'),
        linksSentTo: Translate('view-order/basic-order-info/film/links-sent/text'),
        sendLinksAsEmailButton: Translate('view-order/basic-order-info/film/send-button/text'),
        sendLinksAsEmailPlaceholder: Translate(
            'view-order/basic-order-info/film/send-button/placeholder'
        ),
        sendLinksAsEmailSuccess: Translate('view-order/basic-order-info/film/links-sent/success'),
        sendLinksAsEmailFailure: Translate('view-order/basic-order-info/film/links-sent/failure'),
        sendEmailDynamicError: (translationKey: string) => Translate(translationKey)
    };

    return <VideoHandling {...props} labels={labels} />;
}

export default TranslatedVideoHandling;
